import type {
  ActionPerformed,
  PushNotificationSchema,
} from '@capacitor/push-notifications';
import { useIonRouter } from '@ionic/react';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useNotificationMutations } from '@/core/hooks/use-notifications';
import { navigateShellDeepLink } from '@/core/lib/navigate-shell-deep-link';
import type { ShellIonRouter } from '@/core/lib/navigate-shell-deep-link';
import { initializePushNotifications } from '@/core/lib/push-notifications';
import { showNotificationToast } from '@/core/lib/show-notification-toast';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { canonicalizeShellPathname } from '@/core/router/app-paths';
import { AuthStatus, useAuthStore } from '@/core/store/auth.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

function readPushNotificationPayload(notification: PushNotificationSchema): {
  deepLink?: string;
  notificationId?: string;
  title: string;
  body?: string;
} {
  const data = notification.data ?? {};
  return {
    deepLink: typeof data.deepLink === 'string' ? data.deepLink : undefined,
    notificationId:
      typeof data.notificationId === 'string' && data.notificationId
        ? data.notificationId
        : undefined,
    title: notification.title ?? 'Notification',
    body: notification.body,
  };
}

function readPushActionPayload(action: ActionPerformed): {
  deepLink?: string;
  notificationId?: string;
  workspaceId?: string;
} {
  const data = action.notification.data ?? {};
  return {
    deepLink: typeof data.deepLink === 'string' ? data.deepLink : undefined,
    notificationId:
      typeof data.notificationId === 'string' && data.notificationId
        ? data.notificationId
        : undefined,
    workspaceId:
      typeof data.workspaceId === 'string' && data.workspaceId
        ? data.workspaceId
        : undefined,
  };
}

export function usePushNotifications() {
  const router = useIonRouter();
  const location = useLocation();
  const dict = useDictionary();
  const authStatus = useAuthStore((state) => state.authStatus);
  const hydrated = useAuthStore((state) => state.hydrated);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken);
  const { markRead } = useNotificationMutations();
  const viewLabel = dict.workspace.notifications.view;

  const getNavigationContext = useCallback(
    () => ({
      currentPathname: canonicalizeShellPathname(location.pathname),
      currentSearch: location.search,
      isNative: isCapacitorNative(),
    }),
    [location.pathname, location.search],
  );

  useEffect(() => {
    if (!isCapacitorNative() || !hydrated) return;
    if (authStatus !== AuthStatus.AUTHENTICATED || !accessToken) return;

    void initializePushNotifications({
      onReceived: (notification) => {
        const payload = readPushNotificationPayload(notification);
        showNotificationToast({
          notificationId: payload.notificationId,
          title: payload.title,
          body: payload.body,
          deepLink: payload.deepLink,
          viewLabel,
          onMarkRead: markRead,
          onNavigate: (path) => {
            void navigateShellDeepLink(
              router as ShellIonRouter,
              path,
              getNavigationContext(),
            );
          },
        });
      },
      onAction: (action) => {
        const { deepLink, notificationId } = readPushActionPayload(action);
        if (deepLink) {
          void navigateShellDeepLink(
            router as ShellIonRouter,
            deepLink,
            getNavigationContext(),
          );
        }
        if (notificationId) {
          void markRead(notificationId);
        }
      },
    });
  }, [
    authStatus,
    accessToken,
    hydrated,
    markRead,
    router,
    viewLabel,
    getNavigationContext,
  ]);
}

export { revokePushTokenOnLogout } from '@/core/lib/push-notifications';
